import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Gauge, AlertCircle, Info, Wind, CircleDot, Mountain, TrendingUp } from 'lucide-react';
import { DEFAULT_INPUTS, FIELD_SPECS, validateField } from '../../lib/tractionPowerTools';
import { tractionPowerToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;

const SPEC_BY_KEY = Object.fromEntries(FIELD_SPECS.map((s) => [s.key, s]));

const GROUPS = [
  { id: 'motion', title: 'Speed & Motion', keys: ['speedKmph', 'gradePercent', 'accelerationMs2'] },
  { id: 'vehicle', title: 'Vehicle & Aerodynamics', keys: ['vehicleMassKg', 'dragCoefficient', 'frontalAreaM2', 'rollingResistanceCoefficient'] },
  { id: 'drivetrain', title: 'Drivetrain', keys: ['drivetrainEfficiencyPercent'] },
];

const BREAKDOWN_ROWS = [
  { key: 'aero', label: 'Aerodynamic Drag', icon: Wind },
  { key: 'rollingResistance', label: 'Rolling Resistance', icon: CircleDot },
  { key: 'grade', label: 'Gradient', icon: Mountain },
  { key: 'inertia', label: 'Inertia', icon: TrendingUp },
];

function toRawStrings(defaults) {
  return Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, String(v)]));
}

const TractionPowerCalculator = () => {
  const [raw, setRaw] = useState(() => toRawStrings(DEFAULT_INPUTS));

  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const spec of FIELD_SPECS) {
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
      else parsed[spec.key] = Number(raw[spec.key]);
    }
    return { fieldErrors: errors, values: parsed };
  }, [raw]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasErrors) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      tractionPowerToolApi.calculate(values)
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [values, hasErrors]);

  const renderField = (key) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    return (
      <div className="space-y-2" key={key}>
        <Label htmlFor={`traction-${key}`} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {spec.label} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
        </Label>
        <Input
          id={`traction-${key}`}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          aria-invalid={!!error}
          aria-describedby={error ? `traction-${key}-error` : undefined}
          data-testid={`traction-${key}-input`}
        />
        {error && (
          <p id={`traction-${key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`traction-${key}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="technical-card lg:col-span-3">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <Gauge className="h-5 w-5 text-autoxium-blue" />
            Traction Power Calculator
          </CardTitle>
          <CardDescription>
            Estimate the traction force and power required at the wheels from vehicle speed,
            road gradient, acceleration, mass, aerodynamics, and rolling resistance.
            Results update live as you edit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {GROUPS.map((group) => (
            <div key={group.id} className="space-y-4 p-4 bg-muted/50 rounded-md">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {group.title}
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {group.keys.map(renderField)}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="lg:col-span-2 space-y-4">
        <Card className="technical-card h-fit">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Required Traction Force &amp; Power</CardTitle>
          </CardHeader>
          <CardContent>
            {hasErrors ? (
              <p className="text-sm text-muted-foreground" data-testid="traction-results-placeholder">
                Fix the highlighted fields to see results.
              </p>
            ) : calcError ? (
              <div className="flex items-center gap-2 text-sm text-destructive" data-testid="traction-calc-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
              </div>
            ) : !results ? (
              <p className="text-sm text-muted-foreground" data-testid="traction-results-loading">
                Calculating…
              </p>
            ) : (
              <div className="space-y-4" data-testid="traction-results">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Required Traction Force (Total)</p>
                  <p className="font-mono text-lg font-bold text-autoxium-blue">
                    {results.forcesN.tractionTotal.toFixed(1)} N
                  </p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Required Traction Power (Total, at Wheels)</p>
                  <p className="font-mono text-lg font-bold text-autoxium-blue">
                    {results.powersKw.tractionTotal.toFixed(2)} kW
                  </p>
                </div>

                {results.isCoasting && (
                  <div className="flex items-start gap-2 text-xs text-autoxium-blue bg-autoxium-blue/5 border border-autoxium-blue/20 rounded-md p-3" data-testid="traction-coasting-note">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>
                      Negative traction force/power — the vehicle can coast or regenerate rather
                      than needing driveline power; the resisting forces are being overcome by
                      gravity or deceleration instead.
                    </span>
                  </div>
                )}

                <div className="pt-4 border-t border-structure">
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Engine Power <span className="normal-case text-muted-foreground/70">(w/ {values.drivetrainEfficiencyPercent}% drivetrain efficiency)</span>
                  </p>
                  <p className="font-mono text-sm font-bold">{results.powersKw.engine.toFixed(2)} kW</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {results && (
          <Card className="technical-card" data-testid="traction-breakdown">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight text-base">Force &amp; Power Breakdown</CardTitle>
              <CardDescription>
                Each resisting/driving component computed independently at the given speed.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {BREAKDOWN_ROWS.map((row) => {
                const Icon = row.icon;
                return (
                  <div key={row.key} className="flex items-center justify-between gap-3 pb-3 border-b border-structure last:border-0 last:pb-0">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-autoxium-blue shrink-0" />
                      <span className="font-chivo text-xs font-bold uppercase tracking-wider">{row.label}</span>
                    </div>
                    <span className="font-mono text-sm text-right">
                      {results.forcesN[row.key].toFixed(1)} N · {results.powersKw[row.key].toFixed(2)} kW
                    </span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        <Card className="technical-card" data-testid="traction-sources">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Sources &amp; Assumptions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs text-muted-foreground leading-relaxed">
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">method</Badge>
              Standard longitudinal vehicle dynamics: F_traction = F_aero + F_rolling + F_grade +
              F_inertia, P_traction = F_traction·v. Air density (1.225 kg/m³) and gravitational
              acceleration (9.81 m/s²) are held constant; road gradient is converted from a %
              grade input to an angle via atan(grade% ÷ 100).
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle border-amber-500/50 text-amber-700">verify</Badge>
              The default 0.30 drag coefficient and 2.20 m² frontal area represent a typical
              mid-size passenger car body — use your actual vehicle's published Cd and frontal
              area for a real design.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle border-amber-500/50 text-amber-700">verify</Badge>
              The default 0.012 rolling resistance coefficient sits within the typical
              SAE J1269-measured range for passenger tires (~0.007–0.014) — override with your
              tire's datasheet Crr where available.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">behavior</Badge>
              A negative Required Traction Force/Power means gravity (downhill grade) or
              deceleration is already overcoming the resisting forces — the vehicle can coast or
              regenerate rather than draw driveline power. Engine Power reflects this: it divides
              by drivetrain efficiency when traction power is positive, and multiplies by it when
              coasting/regenerating.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">defaults</Badge>
              80 km/h, flat road (0% grade), steady speed (0 m/s² acceleration), 1500 kg vehicle
              mass, and 90% drivetrain efficiency are typical mid-size passenger car cruise
              conditions, not mandated standards — all inputs are editable for your own vehicle
              and scenario.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TractionPowerCalculator;
